import { useState, useEffect } from 'react';
import {
  Stack,
  TextField,
  Button,
  Typography,
  IconButton,
  Modal,
  Box,
  CircularProgress,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CategoryIcon from '@mui/icons-material/Category';
import PersonIcon from '@mui/icons-material/Person';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { v4 as uuidv4 } from 'uuid';

import { ActionItem } from '@urbanmix-tech/shared-js';
import { uploadUnitAttachment } from '@/data';
import { useAuth } from '@/shared/context/AuthProvider';
import { useAlert } from '@/contexts/Alert/AlertContext';
import { useUIContext, useLoading } from '@/shared/context/UIContextProvider';
import { useSignedUrls } from '@/shared/hooks/useSignedUrls';
import { FileUpload } from '@/components/shared/FileUploads/FileUpload';
import { TaskImage } from './InspectScopeContent';
import { SCOPE_CATEGORIES } from './ActionItemModal/ActionItemModal';

export interface WorkDoneResponse {
  afterImages?: TaskImage[];
  completionDate?: string | null;
  additionalNotes?: string;
  category?: string;  // SCOPE category (e.g., 'windows', 'plumbing') passed through from inspect stage
}

interface WorkDoneContentProps {
  actionItem: ActionItem;
  onChange?: (data: WorkDoneResponse) => void;
}

export function WorkDoneContent({ actionItem, onChange }: WorkDoneContentProps) {
  const { usersData } = useAuth();
  const { showAlert } = useAlert();
  const { openConfirm } = useUIContext();
  const { createLoading } = useLoading();

  const existingResponse = actionItem.response || {};

  // Read-only data from inspect phase
  const beforeImages: TaskImage[] = existingResponse.images || [];
  const beforeSignedUrls = useSignedUrls(beforeImages);

  // Editable form state for this stage
  const [afterImages, setAfterImages] = useState<TaskImage[]>(existingResponse.afterImages || []);
  const [completionDate, setCompletionDate] = useState<string>(
    existingResponse.completionDate ? new Date(existingResponse.completionDate).toISOString().split('T')[0] : ''
  );
  const [additionalNotes, setAdditionalNotes] = useState(existingResponse.additionalNotes || '');

  // UI state
  const [selectedImage, setSelectedImage] = useState<TaskImage | null>(null);
  const [showUpload, setShowUpload] = useState(false);

  const afterSignedUrls = useSignedUrls(afterImages);

  // Report changes to parent (only NEW fields this stage adds + category passthrough)
  useEffect(() => {
    if (onChange) {
      const data = {
        afterImages,
        completionDate: completionDate ? new Date(completionDate).toISOString() : null,
        additionalNotes,
        category: existingResponse.category,
      };
      onChange(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [afterImages, completionDate, additionalNotes]);

  // Get user name from ID
  const getUserName = (userId: string) => {
    if (!userId) return 'Unassigned';
    const user = usersData[userId];
    return user?.fullName || user?.email || 'Unknown';
  };

  // Get category label from value
  const getCategoryLabel = (value: string) => {
    const cat = SCOPE_CATEGORIES.find((c) => c.value === value);
    return cat?.label || value || 'Not specified';
  };

  const handleDeleteImage = (imageId: string) => {
    openConfirm({
      title: 'Delete Image',
      description: 'Are you sure you want to delete this image?',
      onAction: (confirmed) => {
        if (confirmed) {
          setAfterImages((prev) => prev.filter((img) => img.id !== imageId));
          if (selectedImage?.id === imageId) {
            setSelectedImage(null);
          }
        }
      },
    });
  };

  const handleUpload = async (file: File) => {
    const loader = createLoading();
    loader.startLoading({ title: 'Uploading image...' });

    try {
      const url = await uploadUnitAttachment(
        file,
        file.name,
        actionItem.projectId,
        actionItem.id
      );

      if (url) {
        const newImage: TaskImage = {
          id: uuidv4(),
          url,
          type: 'after',
        };
        setAfterImages((prev) => [...prev, newImage]);
        setShowUpload(false);
        showAlert('Image uploaded successfully', 'success');
      } else {
        showAlert('Failed to upload image', 'error');
      }
    } catch (err) {
      console.error('Upload error:', err);
      showAlert('Failed to upload image', 'error');
    } finally {
      loader.stopLoading();
    }
  };

  // Full-screen image overlay
  if (selectedImage) {
    const isAfterImage = afterImages.some((img) => img.id === selectedImage.id);
    const displayUrl = isAfterImage
      ? afterSignedUrls[selectedImage.id] || selectedImage.url
      : beforeSignedUrls[selectedImage.id] || selectedImage.url;

    return (
      <Modal open onClose={() => setSelectedImage(null)}>
        <Box
          className="absolute left-1/2 top-1/2 h-[90%] w-[90%] -translate-x-1/2 -translate-y-1/2 rounded-lg border border-primary bg-black p-4"
          onClick={() => setSelectedImage(null)}
        >
          <IconButton
            onClick={() => setSelectedImage(null)}
            className="absolute right-4 top-4 z-10 text-white"
          >
            <ClearIcon fontSize="large" />
          </IconButton>
          <img
            src={displayUrl}
            alt=""
            className="h-full w-full object-contain"
            onClick={(e) => e.stopPropagation()}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </Box>
      </Modal>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Read-only section: Data from Inspect phase */}
      <div className="rounded-lg bg-gray-800/50 p-12">
        {/* <Typography variant="subtitle2" className="mb-3 text-stone-300">
          Scope Assignment (from Inspector)
        </Typography> */}

        {/* Before image thumbnail */}
        <Typography variant="caption" className="mb-2 block text-stone-300">
          Before Photos
        </Typography>
        {beforeImages.length > 0 && (
          <div className="w-full flex flex-col gap-2">
            {beforeImages.map((img) => {
              const displayUrl = beforeSignedUrls[img.id] || '';
              return (
                <div key={img.id} className="relative w-full">
                  {displayUrl ? (
                    <img
                      src={displayUrl}
                      alt=""
                      className="w-full h-[200px] cursor-pointer rounded object-cover hover:opacity-80"
                      onClick={() => setSelectedImage(img)}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="flex w-full h-[200px] items-center justify-center rounded bg-gray-700">
                      <CircularProgress size={20} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Read-only info grid */}
        {/* <Stack spacing={0.5} className="grid grid-cols-2 cursor-default">
          <Stack direction="row" alignItems="center" gap={1}>
            <CategoryIcon sx={{ fontSize: 18, color: 'gray' }} />
            <Typography variant="body2" className="text-stone-300">
              Category:
            </Typography>
            <Typography variant="body2" className="text-white">
              {getCategoryLabel(existingResponse.category)}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" gap={1}>
            <PersonIcon sx={{ fontSize: 18, color: 'gray' }} />
            <Typography variant="body2" className="text-stone-300">
              Assigned To:
            </Typography>
            <Typography variant="body2" className="text-white">
              {getUserName(existingResponse.assignedTo)}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" gap={1}>
            <PriorityHighIcon sx={{ fontSize: 18, color: 'gray' }} />
            <Typography variant="body2" className="text-stone-300">
              Priority:
            </Typography>
            <Typography variant="body2" className="capitalize text-white">
              {existingResponse.priority || 'Medium'}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" gap={1}>
            <CalendarTodayIcon sx={{ fontSize: 18, color: 'gray' }} />
            <Typography variant="body2" className="text-stone-300">
              Target Date:
            </Typography>
            <Typography variant="body2" className="text-white">
              {existingResponse.targetDate
                ? new Date(existingResponse.targetDate).toLocaleDateString()
                : '-'}
            </Typography>
          </Stack>
        </Stack> */}

        {existingResponse.notes && (
          <div className="mt-3">
            <Typography variant="body2" className="text-stone-300">
              Inspector Notes:
            </Typography>
            <Typography variant="body2" className="mt-1 text-white">
              {existingResponse.notes}
            </Typography>
          </div>
        )}
      </div>

      {/* Editable section: After image upload */}
      <div className="rounded-lg bg-gray-800/50 p-12">
        <Typography variant="caption" className="mb-2 block text-stone-300">
          After Photos
        </Typography>

        {afterImages.length > 0 && (
          <div className="w-full flex flex-col gap-2">
            {afterImages.map((img) => {
              const displayUrl = afterSignedUrls[img.id] || '';
              return (
                <div key={img.id} className="relative w-full">
                  {displayUrl ? (
                    <img
                      src={displayUrl}
                      alt=""
                      className="w-full h-[200px] cursor-pointer rounded object-cover hover:opacity-80"
                      onClick={() => setSelectedImage(img)}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="flex w-full h-[200px] items-center justify-center rounded bg-gray-700">
                      <CircularProgress size={20} />
                    </div>
                  )}
                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    className="mt-1"
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteImage(img.id)}
                    >
                      <DeleteIcon className="text-red-400" fontSize="small" />
                    </IconButton>
                  </Stack>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showUpload ? (
        <div className="mb-3">
          <FileUpload onChangeFile={handleUpload} />
          <Button
            size="small"
            onClick={() => setShowUpload(false)}
            className="mt-2 text-gray-400"
          >
            Cancel
          </Button>
        </div>
      ) : (
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileUploadIcon />}
          onClick={() => setShowUpload(true)}
          className="!border-gray-600 !text-gray-300"
        >
          {afterImages.length > 0 ? 'Add More Photos' : 'Upload After Photos'}
        </Button>
      )}

      <hr className="border-gray-700 pb-4" />

      {/* Completion date and worker notes */}
      <TextField
        label="Completion Date"
        type="date"
        value={completionDate}
        onChange={(e) => setCompletionDate(e.target.value)}
        fullWidth
        size="small"
        InputLabelProps={{ shrink: true }}
      />

      <TextField
        label="Additional Notes"
        multiline
        rows={3}
        value={additionalNotes}
        onChange={(e) => setAdditionalNotes(e.target.value)}
        fullWidth
        size="small"
        placeholder="Additional notes for payment processing..."
      />
    </Stack>
  );
}
